import type { Event, State } from './types';

export function transition(state: State, event: Event, now: number, devMode = true): State {
  switch (state.kind) {
    case 'loading': {
      if (event.type === 'SPLAT_READY') {
        return { kind: 'at-hotspot', index: 0, drifted: false, driftedTarget: null };
      }
      return illegal(state, event, devMode);
    }

    case 'at-hotspot': {
      switch (event.type) {
        case 'GOTO': {
          if (event.index === state.index) {
            if (state.drifted) {
              return {
                kind: 'animating',
                fromIndex: state.index,
                toIndex: event.index,
                startedAt: now,
                drifted: false,
                driftedTarget: null
              };
            }
            return state;
          }
          return {
            kind: 'animating',
            fromIndex: state.index,
            toIndex: event.index,
            startedAt: now,
            drifted: false,
            driftedTarget: null
          };
        }
        case 'OPEN_LIGHTBOX':
          return {
            kind: 'lightbox',
            index: state.index,
            src: event.src,
            drifted: state.drifted,
            driftedTarget: state.driftedTarget
          };
        case 'OPEN_PANO':
          return {
            kind: 'pano',
            index: state.index,
            src: event.src,
            drifted: state.drifted,
            driftedTarget: state.driftedTarget
          };
        case 'DOUBLE_CLICK':
          return {
            kind: 'at-hotspot',
            index: state.index,
            drifted: true,
            driftedTarget: event.worldPoint
          };
        case 'RECENTER':
          return {
            kind: 'animating',
            fromIndex: state.index,
            toIndex: state.index,
            startedAt: now,
            drifted: false,
            driftedTarget: null
          };
        case 'ESC':
          return state;
        case 'CLOSE_LIGHTBOX':
        case 'CLOSE_PANO':
        case 'ANIMATION_DONE':
        case 'SPLAT_READY':
          return illegal(state, event, devMode);
      }
      return state;
    }

    case 'animating': {
      switch (event.type) {
        case 'ANIMATION_DONE':
          return {
            kind: 'at-hotspot',
            index: state.toIndex,
            drifted: false,
            driftedTarget: null
          };
        case 'GOTO':
          return {
            kind: 'animating',
            fromIndex: state.fromIndex,
            toIndex: event.index,
            startedAt: now,
            drifted: false,
            driftedTarget: null
          };
        // The panel renders the destination hotspot's content during the
        // camera flight, so photo / 360 buttons are visible and clickable
        // before ANIMATION_DONE fires. Treat the destination as authoritative
        // and open the overlay — the camera-mode subscriber switches to idle
        // for both lightbox/pano states, so the in-flight animation freezes
        // silently underneath the overlay. On CLOSE_*, the at-hotspot
        // subscriber re-arms orbit mode at the destination target.
        case 'OPEN_LIGHTBOX':
          return {
            kind: 'lightbox',
            index: state.toIndex,
            src: event.src,
            drifted: false,
            driftedTarget: null
          };
        case 'OPEN_PANO':
          return {
            kind: 'pano',
            index: state.toIndex,
            src: event.src,
            drifted: false,
            driftedTarget: null
          };
        case 'ESC':
          return state;
        default:
          return illegal(state, event, devMode);
      }
    }

    case 'lightbox': {
      if (event.type === 'CLOSE_LIGHTBOX' || event.type === 'ESC') {
        return {
          kind: 'at-hotspot',
          index: state.index,
          drifted: state.drifted,
          driftedTarget: state.driftedTarget
        };
      }
      return illegal(state, event, devMode);
    }

    case 'pano': {
      if (event.type === 'CLOSE_PANO' || event.type === 'ESC') {
        return {
          kind: 'at-hotspot',
          index: state.index,
          drifted: state.drifted,
          driftedTarget: state.driftedTarget
        };
      }
      return illegal(state, event, devMode);
    }
  }
}

function illegal(state: State, event: Event, devMode: boolean): State {
  if (devMode) {
    throw new Error(`Illegal transition: ${state.kind} + ${event.type}`);
  }
  return state;
}
